import { motion } from 'framer-motion';
import { PillarIcon } from '../../lib/icons';
import { HeroVideo } from '../HeroVideo';
import type { HomeProps } from './types';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 6 } as const,
  animate: { opacity: 1, y: 0 } as const,
  transition: { delay, duration: 0.4 },
});

export function MinimalHome(p: HomeProps) {
  const videoUrl = p.heroVideoEnabled && p.heroVideoUrl ? p.heroVideoUrl : undefined;
  const v = !!videoUrl;
  return (
    <>
      {/* Hero — compact, no excess whitespace */}
      <div className={`pt-8 ${v ? 'pb-28 -mx-5 px-5 overflow-hidden relative' : 'pb-6'}`}>
        {videoUrl ? <HeroVideo url={videoUrl} /> : null}
        <motion.div {...fade()} className={`text-center space-y-3 ${v ? 'relative z-[2]' : ''}`}>
          <span className={`inline-flex items-center gap-2 text-[10px] tracking-[0.3em] font-bold uppercase rounded-full px-3 py-1 ${v ? 'text-gold-soft bg-white/10 backdrop-blur-md border border-white/20' : 'text-gold-deep bg-cream border border-oud-100/40'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            {p.instantBadge}
          </span>

          <motion.h1 {...fade(0.06)}
            className={`text-[2.2rem] md:text-[2.6rem] font-black leading-[1.1] whitespace-pre-line ${v ? 'text-white drop-shadow-md' : 'text-oud-900'}`}>
            {p.headline}
          </motion.h1>

          <motion.p {...fade(0.1)}
            className={`text-sm leading-relaxed ${v ? 'text-white/85 drop-shadow-sm' : 'text-oud-600'}`}>
            {p.subheadline}
          </motion.p>
        </motion.div>

        {v ? (
          <motion.div {...fade(0.14)} className="relative z-[2] space-y-2 mt-4 max-w-xs mx-auto">
            <button type="button" onClick={p.onCamera}
              className="cta-primary w-full backdrop-blur-lg text-white py-3.5 rounded-2xl font-bold text-base active:scale-[0.98]">
              {p.ctaLabel}
            </button>
            <button type="button" onClick={p.onGallery}
              className="w-full text-sm text-white/70 hover:text-white font-bold py-2 transition-colors text-center">
              {p.ctaUploadLabel}
            </button>
          </motion.div>
        ) : null}
      </div>

      {v ? null : (
        <motion.div {...fade(0.14)} className="space-y-2">
          <button type="button" onClick={p.onCamera}
            className="w-full h-13 py-3.5 rounded-2xl font-bold text-base bg-oud-900 text-sand shadow-soft hover:shadow-quote active:scale-[0.98] transition-all">
            {p.ctaLabel}
          </button>
          <button type="button" onClick={p.onGallery}
            className="w-full text-sm text-oud-500 hover:text-oud-800 font-bold py-2 transition-colors text-center">
            {p.ctaUploadLabel}
          </button>
        </motion.div>
      )}

      {/* Pillars — compact 2×2 grid */}
      <motion.div {...fade(0.2)} className="mt-7 grid grid-cols-2 gap-2">
        {p.pillars.map((pl, i) => (
          <motion.div
            key={pl.label}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.24 + i * 0.05 }}
            className="flex items-center gap-2.5 rounded-xl bg-cream/70 border border-oud-100/40 px-3 py-2.5"
          >
            <div className="flex-none w-8 h-8 rounded-lg bg-oud-900/5 text-gold-deep flex items-center justify-center">
              <PillarIcon name={pl.icon} />
            </div>
            <span className="text-[12px] font-bold text-oud-800 leading-tight">{pl.label}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* Steps — simple, tight */}
      <section className="mt-10">
        <motion.h2 {...fade(0.35)} className="text-center text-[11px] font-bold text-oud-500 tracking-[0.25em] uppercase mb-5">
          {p.stepsTitle}
        </motion.h2>
        <div className="space-y-3">
          {p.steps.map((step, i) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.07 }}
              className="flex gap-3 items-start"
            >
              <span className="flex-none w-7 h-7 rounded-lg bg-cream border border-oud-100/50 text-oud-700 flex items-center justify-center font-black text-xs">
                {i + 1}
              </span>
              <p className="text-sm text-oud-700 leading-relaxed pt-0.5">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* External link */}
      {p.websiteUrl ? (
        <motion.section {...fade(0.6)} className="mt-9">
          <a href={p.websiteUrl} target="_blank" rel="noreferrer noopener"
            className="flex items-center justify-between gap-3 rounded-2xl bg-cream/70 border border-oud-100/40 p-4 hover:border-oud-200 transition-colors group">
            <div>
              <span className="block text-[9px] tracking-[0.3em] text-oud-400 font-bold uppercase">{p.brandName}</span>
              <span className="block text-base font-black text-oud-900 mt-0.5 group-hover:text-gold-deep transition-colors">{p.websiteLabel}</span>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
              className="w-4 h-4 text-oud-400 group-hover:text-gold transition-colors flex-none" aria-hidden>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14L21 3" />
            </svg>
          </a>
        </motion.section>
      ) : null}

      {/* WhatsApp */}
      {p.showWhatsapp && p.waNumber ? (
        <motion.section {...fade(0.65)} className="mt-5 mb-4">
          <a href={`https://wa.me/${p.waNumber}`} target="_blank" rel="noreferrer noopener"
            className="flex items-center justify-center gap-2.5 border border-oud-800 text-oud-900 rounded-2xl h-13 py-3.5 px-6 text-sm font-bold hover:bg-oud-900 hover:text-sand transition-all active:scale-[0.98] w-full">
            {p.waButtonLabel}
          </a>
        </motion.section>
      ) : null}
    </>
  );
}
